import type { GoldenGenerateParams, GoldenGenerateResponse } from '../golden/goldenTransport';
import {
  logOriginalCompatRetryAudit,
  type OriginalCompatRetryAuditEntry,
} from '../golden/goldenGenerateWithRetry';
import type { OriginalEngineAITransport, OriginalEngineTransportContext } from '../types';
import { ORIGINAL_COMPAT_CLIENT_HTTP_ATTEMPTS_PER_LOGICAL_CALL } from './originalCompatRetryPolicy';

export interface BoundedCompatTransportOptions {
  inner: OriginalEngineAITransport;
  maxClientTransportAttempts?: number;
  onAudit?: (entry: OriginalCompatRetryAuditEntry) => void;
}

/**
 * Wraps a transport with bounded client attempts and retry audit logging.
 * One logical callId → at most N client transport attempts (default 1).
 */
export class BoundedCompatTransport implements OriginalEngineAITransport {
  private readonly audits: OriginalCompatRetryAuditEntry[] = [];

  constructor(private readonly options: BoundedCompatTransportOptions) {}

  getAuditLog(): OriginalCompatRetryAuditEntry[] {
    return [...this.audits];
  }

  async generate<TRequest, TResponse>(
    request: TRequest,
    context: OriginalEngineTransportContext,
  ): Promise<TResponse> {
    const maxAttempts =
      this.options.maxClientTransportAttempts ?? ORIGINAL_COMPAT_CLIENT_HTTP_ATTEMPTS_PER_LOGICAL_CALL;
    let lastError: unknown;
    for (let clientAttempt = 1; clientAttempt <= maxAttempts; clientAttempt += 1) {
      try {
        const response = await this.options.inner.generate<TRequest, TResponse>(request, context);
        const entry: OriginalCompatRetryAuditEntry = {
          agent: context.agent,
          logicalCallId: context.logicalCallId,
          originalLogicalAttempt: 1,
          clientHttpAttempt: clientAttempt,
          serverUpstreamAttempt: 1,
          maximumServerUpstreamAttempts: 1,
          maximumTotalUpstreamAttemptsForAgentFunction: 5,
        };
        this.audits.push(entry);
        logOriginalCompatRetryAudit(entry);
        this.options.onAudit?.(entry);
        return response;
      } catch (error) {
        lastError = error;
        if (clientAttempt >= maxAttempts) break;
      }
    }
    throw lastError;
  }
}

export function createCompatTransportStack(
  inner: OriginalEngineAITransport,
  onAudit?: (entry: OriginalCompatRetryAuditEntry) => void,
): { transport: OriginalEngineAITransport; getAudits: () => OriginalCompatRetryAuditEntry[] } {
  const bounded = new BoundedCompatTransport({ inner, onAudit });
  return {
    transport: bounded,
    getAudits: () => bounded.getAuditLog(),
  };
}

export type LogicalCallRegistry = {
  beginLogicalCall: (
    agent: OriginalEngineTransportContext['agent'],
    base: Pick<OriginalEngineTransportContext, 'projectId' | 'chapterNumber'>,
  ) => { logicalCallId: string; context: OriginalEngineTransportContext };
};

export function createLogicalCallRegistry(): LogicalCallRegistry {
  const counters = new Map<string, number>();
  return {
    beginLogicalCall(agent, base) {
      const key = `${base.projectId}:${base.chapterNumber}:${agent}`;
      const next = (counters.get(key) || 0) + 1;
      counters.set(key, next);
      const logicalCallId = `${key}:${next}`;
      return {
        logicalCallId,
        context: {
          agent,
          projectId: base.projectId,
          chapterNumber: base.chapterNumber,
          logicalCallId,
        },
      };
    },
  };
}

export function bindGoldenGenerateWithLogicalCalls(
  transport: OriginalEngineAITransport,
  base: Pick<OriginalEngineTransportContext, 'projectId' | 'chapterNumber'>,
  bindFn: (fn: (params: GoldenGenerateParams) => Promise<GoldenGenerateResponse>) => void,
  inferAgent: (params: GoldenGenerateParams) => OriginalEngineTransportContext['agent'],
): LogicalCallRegistry {
  const registry = createLogicalCallRegistry();
  bindFn(async (params) => {
    const agent = inferAgent(params);
    const { context } = registry.beginLogicalCall(agent, base);
    return transport.generate<GoldenGenerateParams, GoldenGenerateResponse>(params, context);
  });
  return registry;
}
